import { useState, useEffect } from 'react';

const STORAGE_KEY = 'dhana_progress';

const defaultProgress = {
  completedLessons: [],
  quizScores: {},
  streak: 0,
  lastVisit: null,
  totalScore: 0
};

export function useProgress() {
  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultProgress;
    } catch {
      return defaultProgress;
    }
  });

  useEffect(() => {
    // Update streak on every visit
    const today = new Date().toDateString();
    const lastVisit = progress.lastVisit;

    if (lastVisit !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const wasYesterday = lastVisit === yesterday.toDateString();

      setProgress(prev => {
        const updated = {
          ...prev,
          lastVisit: today,
          streak: wasYesterday ? prev.streak + 1 : lastVisit ? 1 : prev.streak || 1
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, []);

  const completeLesson = (lessonId, score) => {
    setProgress(prev => {
      const updated = {
        ...prev,
        completedLessons: prev.completedLessons.includes(lessonId)
          ? prev.completedLessons
          : [...prev.completedLessons, lessonId],
        quizScores: { ...prev.quizScores, [lessonId]: score },
        totalScore: Math.round(
          Object.values({ ...prev.quizScores, [lessonId]: score })
            .reduce((a, b) => a + b, 0) /
          Object.keys({ ...prev.quizScores, [lessonId]: score }).length
        )
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const isCompleted = (lessonId) => progress.completedLessons.includes(lessonId);

  const getNextLesson = (lessons) => {
    return lessons.find(l => !progress.completedLessons.includes(l.id)) || null;
  };

  const allDone = (lessons) => {
    return lessons.every(l => progress.completedLessons.includes(l.id));
  };

  const resetProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
    setProgress(defaultProgress);
  };

  return {
    progress,
    completeLesson,
    isCompleted,
    getNextLesson,
    allDone,
    resetProgress
  };
}
